import { z } from 'zod';
import { ActorSchema } from '../common/actor.schema';
import { ProspectStatusSchema } from '../deal/prospects.schemas';

export const BrokerageNoticeSentPayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  emailId: z.uuid(),
  kind: z.enum(['NOTICE', 'REMINDER']),
  recipient: z.email(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type BrokerageNoticeSentPayload = z.infer<typeof BrokerageNoticeSentPayloadSchema>;

export const BrokerageNoticeReminderDuePayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  noticeEmailId: z.uuid(),
  noticeSentAt: z.iso.datetime(),
  // 1 for the first attempt; a transient provider refusal re-publishes the event with the next number.
  attempt: z.number().int().min(1).default(1),
});

export type BrokerageNoticeReminderDuePayload = z.infer<typeof BrokerageNoticeReminderDuePayloadSchema>;

export const BrokerageAgreementConcludedPayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  responsibleMemberId: z.uuid(),
  concludedAt: z.iso.datetime(),
  contractNumber: z.string(),
  earlyStartRequested: z.boolean(),
  fullExposeAvailable: z.boolean(),
  withdrawalPeriodEndsOn: z.iso.date(),
  actor: ActorSchema,
  trigger: z.literal('PROSPECT_PAGE'),
  occurredAt: z.iso.datetime(),
});

export type BrokerageAgreementConcludedPayload = z.infer<typeof BrokerageAgreementConcludedPayloadSchema>;

// Ids and machine fields only (ADR 0015 §PII): names, title and the free-text reason reach the notification through
// BrokerageConclusionResolver, never through this audited event.
// An internal timer, not a domain fact: a transient provider refusal of the confirmation e-mail asks for the next attempt.
export const BrokerageConfirmationRetryDuePayloadSchema = z.object({
  agreementId: z.uuid(),
  workspaceId: z.uuid(),
  propertyId: z.uuid(),
  attempt: z.number().int().min(2),
});

export type BrokerageConfirmationRetryDuePayload = z.infer<typeof BrokerageConfirmationRetryDuePayloadSchema>;

export const BrokerageWithdrawalReceivedPayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  responsibleMemberId: z.uuid(),
  receivedAt: z.iso.datetime(),
  statusBefore: ProspectStatusSchema,
  movedToLost: z.boolean(),
  actor: ActorSchema,
  trigger: z.literal('PROSPECT_PAGE'),
  occurredAt: z.iso.datetime(),
});

export type BrokerageWithdrawalReceivedPayload = z.infer<typeof BrokerageWithdrawalReceivedPayloadSchema>;

export const BrokerageAcceptanceReplyReceivedPayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  contactId: z.uuid(),
  receivedAt: z.iso.datetime(),
  actor: ActorSchema,
  occurredAt: z.iso.datetime(),
});

export type BrokerageAcceptanceReplyReceivedPayload = z.infer<typeof BrokerageAcceptanceReplyReceivedPayloadSchema>;

/** Published at conclusion, delivered three days later: the one acceptance reminder if the reply is still unmarked. */
export const BrokerageAcceptanceReminderDuePayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  propertyId: z.uuid(),
  // 1 for the first attempt; a transient provider refusal re-publishes the event with the next number.
  attempt: z.number().int().min(1).default(1),
});

export type BrokerageAcceptanceReminderDuePayload = z.infer<typeof BrokerageAcceptanceReminderDuePayloadSchema>;

export const BrokerageWithdrawalReceiptRetryDuePayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  attempt: z.number().int().min(2),
});

export type BrokerageWithdrawalReceiptRetryDuePayload = z.infer<typeof BrokerageWithdrawalReceiptRetryDuePayloadSchema>;

export const BrokerageWithdrawalReceiptSettledPayloadSchema = z.object({
  agreementId: z.uuid(),
  prospectId: z.uuid(),
  workspaceId: z.uuid(),
  organizationId: z.uuid(),
  receivedAt: z.iso.datetime(),
  reason: z.string().nullable(),
  receiptState: z.enum(['SENT', 'FAILED']),
  occurredAt: z.iso.datetime(),
});

export type BrokerageWithdrawalReceiptSettledPayload = z.infer<typeof BrokerageWithdrawalReceiptSettledPayloadSchema>;

// An internal timer, not a domain fact: asks the deal module to read one Interessent e-mail's delivery status from
// the provider (IMM-1065.12).
export const BrokerageEmailDeliveryCheckDuePayloadSchema = z.object({
  emailId: z.uuid(),
  agreementId: z.uuid(),
  attempt: z.number().int().min(1).max(6),
  sentAt: z.iso.datetime(),
});

export type BrokerageEmailDeliveryCheckDuePayload = z.infer<typeof BrokerageEmailDeliveryCheckDuePayloadSchema>;
